/**
 * Exponential backoff utility with jitter for retry mechanisms
 */

export interface BackoffOptions {
  /** Initial delay in milliseconds */
  initialDelay?: number;
  /** Maximum delay in milliseconds */
  maxDelay?: number;
  /** Maximum number of retry attempts */
  maxAttempts?: number;
  /** Base multiplier for exponential growth */
  backoffFactor?: number;
  /** Enable jitter to prevent thundering herd */
  jitter?: boolean;
  /** Timeout for each operation attempt in milliseconds */
  timeout?: number;
}

export interface RetryOptions<T> extends BackoffOptions {
  /** Function to execute with retry logic */
  operation: () => Promise<T>;
  /** Optional predicate to determine if error should trigger retry */
  shouldRetry?: (error: Error, attempt: number) => boolean;
  /** Optional callback called on each retry attempt. Can return a Promise to wait before retrying. */
  onRetry?: (error: Error, attempt: number, nextDelay: number) => void | Promise<void>;
  /** Optional AbortSignal to cancel retry attempts */
  abortSignal?: AbortSignal;
}

/**
 * Default configuration for exponential backoff
 */
const DEFAULT_OPTIONS: Required<BackoffOptions> = {
  initialDelay: 500, // Start with 0.5 seconds
  maxDelay: 16000, // Cap at 16 seconds
  maxAttempts: 10, // Max 10 attempts
  backoffFactor: 2, // Double each time
  jitter: true, // Enable jitter by default
  timeout: 20000, // 20 seconds timeout by default
};

/**
 * Calculates the delay for a given attempt using exponential backoff
 */
export function calculateBackoffDelay(
  attempt: number,
  options: BackoffOptions = {}
): number {
  const config = { ...DEFAULT_OPTIONS, ...options };

  // Calculate exponential delay: initialDelay * (backoffFactor ^ attempt)
  let delay = config.initialDelay * Math.pow(config.backoffFactor, attempt);

  // Cap at maximum delay
  delay = Math.min(delay, config.maxDelay);

  // Add jitter to prevent thundering herd
  if (config.jitter) {
    // Add random jitter between 0 and the current delay value
    const jitterAmount = Math.random() * delay;
    delay = delay + jitterAmount;
  }

  return Math.round(delay);
}

/**
 * Creates a delay promise that resolves after the specified milliseconds
 */
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Creates a delay promise that can be aborted
 */
function delayWithAbort(
  milliseconds: number,
  abortSignal?: AbortSignal
): Promise<void> {
  return new Promise((resolve, reject) => {
    // Check if already aborted
    if (abortSignal?.aborted) {
      reject(new Error('Operation was aborted'));
      return;
    }

    const timeoutId = setTimeout(() => {
      resolve();
    }, milliseconds);

    // Set up abort listener
    const abortHandler = () => {
      clearTimeout(timeoutId);
      reject(new Error('Operation was aborted'));
    };

    if (abortSignal) {
      abortSignal.addEventListener('abort', abortHandler, { once: true });

      // Clean up listener when promise resolves
      timeoutId &&
        setTimeout(() => {
          abortSignal.removeEventListener('abort', abortHandler);
        }, milliseconds);
    }
  });
}

/**
 * Creates a timeout promise that rejects after the specified delay
 */
function createTimeoutPromise(timeoutMs: number): Promise<never> {
  return new Promise((_, reject) => {
    setTimeout(() => {
      reject(new Error(`Operation timed out after ${timeoutMs}ms`));
    }, timeoutMs);
  });
}

/**
 * Executes an operation with exponential backoff retry logic
 */
export async function retryWithBackoff<T>(
  options: RetryOptions<T>
): Promise<T> {
  const config = { ...DEFAULT_OPTIONS, ...options };
  let lastError: Error;

  for (let attempt = 0; attempt < config.maxAttempts; attempt++) {
    // Check if operation was aborted before attempting
    if (options.abortSignal?.aborted) {
      throw new Error('Operation was aborted');
    }

    try {
      let operationPromise: Promise<T>;
      if (options.timeout) {
        // Race the operation against a timeout
        const timeoutPromise = createTimeoutPromise(options.timeout);
        operationPromise = Promise.race([options.operation(), timeoutPromise]);
      } else {
        // No timeout, execute operation normally
        operationPromise = options.operation();
      }

      return await operationPromise;
    } catch (error) {
      lastError = error as Error;

      // Check if operation was aborted during execution
      if (options.abortSignal?.aborted) {
        throw new Error('Operation was aborted');
      }

      // Check if we should retry this error
      if (options.shouldRetry && !options.shouldRetry(lastError, attempt)) {
        throw lastError;
      }

      // Don't retry if this was the last attempt
      if (attempt === config.maxAttempts - 1) {
        break;
      }

      // Calculate delay for next attempt
      const nextDelay = calculateBackoffDelay(attempt, config);

      // Call onRetry callback if provided and wait for it to complete
      if (options.onRetry) {
        const retryResult = options.onRetry(lastError, attempt, nextDelay);
        if (retryResult instanceof Promise) {
          await retryResult;
        }
      }

      // Check if operation was aborted before waiting
      if (options.abortSignal?.aborted) {
        throw new Error('Operation was aborted');
      }

      // Wait before next attempt, but check for abortion during delay
      await delayWithAbort(nextDelay, options.abortSignal);
    }
  }

  // All attempts failed
  throw lastError;
}
